/**
 * 大海龟旅店（INN_ARGUMENT → OMEN_END）。真正的 WorldScene 子类：
 *  - 雨中的门口 → 挤进店里（自动观察“店里人很多”）→ 空位「坐下」→ 点苦酒、环顾 → 同桌争论（逐字）→ 不祥预感 → 章末。
 *  - 屋檐（rainShelter）内外切换 rain ⇄ interior-rain；空间闲聊音效（SeededRng）；站着的客人给玩家让路。
 *  - 赵呈博由本场景自己生成并跟随（落座演出前解除跟随，之后由时间线摆位）。
 *  - 正式图集（manifest 组 turtle-inn：actor-luo-dahu / actor-wu-yong / actor-inn-boss / actor-inn-waiter / actor-patron-*）
 *    经 Router 先行装载；姿态导演（innPoseDirector）把台词、表情、零星动作落到图集真有的帧
 *    （坐 sit / 坐着说 sittalk / 暗淡 dim / 大笑 laugh / 举杯 drink / 站着喊 boast / 擦杯 wipe），缺帧时退回气泡与 characters 的回退链。
 *  - 续玩：INN_ARGUMENT 已设则直接落座并进入预感段（不在演出中途续）；OMEN_END 由预感时间线终止步设置后 ctx.enterRoute() → ChapterEnd。
 *  - 所有对白 / 演出数据见 innDialogues.ts / innTimelines.ts；引文唯一来源 innText.ts。
 */
import Phaser from 'phaser';
import { Depth, FONT_BODY, GAME_H, GAME_W } from '@/core/config';
import { bus, Events, type EventPayloads } from '@/core/events';
import { SceneKeys } from '@/core/scenes/keys';
import { SeededRng } from '@/core/rng';
import type { Facing, SceneEntryData, WeatherState } from '@/core/types';
import type { ActorAnimState, IActor, MapExit, MapInteraction } from '@/core/types/contracts';
import { WorldScene, type WorldSceneConfig } from '@/world';
import { Follower, type Actor } from '@/characters';
import { createDialogueRunner, hooksFromContext, resolveDialogueBox, type DialogueHooks, type DialogueRunner } from '@/dialogue';
import { createCinematicPlayer, createSceneCinematicHost, stateFromContext, type CinematicHost, type CinematicPlayer, type CinematicStateAccess } from '@/cinematics';
import { AmbiencePresets, AudioKeys, audio } from '@/audio';
import { registerTurtleInn } from './innRegister';
import { ensureInnDevTextures } from './innDevTextures';
import { CAST, CHATTER_SFX, CHATTER_SOURCES, CROWD_IDS, INN_MAP_ID, INN_NPCS, SEATED_IDS, SEATS, innActorTexture } from './innCast';
import { isSheltered } from './innMap';
import { InnPoseDirector } from './innPoseDirector';
import { aliasWalkFrames, emoteIcon } from './innPoses';

type InteractKind = Exclude<MapInteraction['kind'], 'exit'>;
/** 主线进度：自由探索 → 已落座（演出中）→ 争论已过（预感段）→ 章末。 */
export type InnStage = 'free' | 'seated' | 'argued' | 'done';

export interface InnDebugHandle {
  ready: boolean;
  getState(): {
    stage: InnStage; flag: string | null; weather: WeatherState; sheltered: boolean; locked: boolean;
    player: { x: number; y: number; facing: Facing }; zhao: { x: number; y: number } | null;
    prompt: string | null; dialogue: string | null; cinematic: string | null; lastLine: string | null; actors: number; frozen: boolean;
  };
  /** 走到空位并坐下（= 在空位前按确认）。 */
  sit(): Promise<void>;
  skip(): boolean;
  interact(): string | null;
  teleport(x: number, y: number, facing?: Facing): void;
  /** 只播放预感段（调试）。 */
  omen(): Promise<void>;
  /** id → 正在播放的动画 key（`actor-luo-dahu:dim-down` …）。 */
  poses(): Record<string, string>;
  /** id → 纹理 key（正式 `actor-*` / 开发占位）。 */
  art(): Record<string, string>;
}
declare global { interface Window { __nantianInn?: InnDebugHandle } }

const CHATTER_MIN_MS = 2200;
const CHATTER_MAX_MS = 5200;
const YIELD_DIST = 44;
const UNYIELD_DIST = 110;
const LAUGH_SFX = 'sfx-laughter-burst';

function isCharacterActor(a: IActor | null | undefined): a is Actor {
  const o = a as unknown as Record<string, unknown> | null | undefined;
  return !!o && typeof o['setPositionDriven'] === 'function' && typeof o['setVelocity'] === 'function';
}

export class TurtleInnScene extends WorldScene {
  protected readonly mapId = INN_MAP_ID;

  private dialogue: DialogueRunner | null = null;
  private cine: CinematicPlayer | null = null;
  private director: InnPoseDirector | null = null;
  private zhao: IActor | null = null;
  private zhaoFollower: Follower | null = null;
  private alive = false;
  private showcase = false;
  private mainBusy = false;
  private stage: InnStage = 'free';
  private sheltered = false;
  private frozen = false;
  private chatterRng = new SeededRng('inn-chatter');
  private chatterLeft = 1500;
  private readonly yielded = new Set<string>();
  private readonly wrapped = new Map<string, { inner: IActor; proxy: IActor }>();
  private lastLine: string | null = null;
  private hint: Phaser.GameObjects.Text | null = null;
  /** showcase / 无剧情状态时的本地旗标与标记（不碰真实剧情）。 */
  private readonly localFlags = new Set<string>();
  private readonly localMarks = new Set<string>();
  private readonly onLine = (p: EventPayloads[typeof Events.DIALOGUE_LINE]): void => { this.lastLine = p.text; };
  private readonly onDialogueEnd = (): void => { this.director?.end(); };

  constructor() { super(SceneKeys.TURTLE_INN); }

  // ------------------------------------------------------------------ WorldScene hooks
  protected override worldConfig(): WorldSceneConfig {
    return { player: { id: CAST.player, actor: 'jiang-zichao' }, followers: [], defaultSpawn: 'inn-entrance', weatherTransitionMs: 1200 };
  }

  protected override ambienceFor(state: WeatherState): { key: string; volume: number }[] {
    const A = AudioKeys.ambience;
    if (state === 'interior-rain') return [{ key: A.rainShell, volume: 0.8 }, { key: A.tavernChatter, volume: 0.9 }, { key: A.rainLight, volume: 0.3 }];
    return [{ key: A.rainHeavy, volume: 0.9 }, { key: A.tavernChatter, volume: 0.45 }, { key: A.wind, volume: 0.3 }];
  }

  protected override onPreCreate(): void {
    registerTurtleInn();
    ensureInnDevTextures(this);
    // 正式图集适配：小二没有 walk 帧，端酒（carry）帧充当行走（幂等；图集未装载时不做事）。
    aliasWalkFrames(this, innActorTexture(CAST.waiter));
    this.showcase = this.ctx?.url.showcase === 'turtle-inn';
    this.alive = true;
    this.mainBusy = false;
    this.frozen = false;
    this.stage = 'free';
    this.lastLine = null;
    this.yielded.clear();
    this.wrapped.clear();
    this.localFlags.clear();
    this.localMarks.clear();
    this.chatterRng = new SeededRng(SeededRng.hash(`${this.ctx?.seed ?? 1157}:inn-chatter`));
    this.chatterLeft = 1500;
  }

  protected override onCreate(): void {
    this.cameras.main.setZoom(1);
    // 天气：故事到这里已经下雨；屋檐下用 interior-rain（暖灯笼调），檐外 rain。
    const p = this.player;
    this.sheltered = isSheltered(p.x, p.y, this.map);
    this.setWeather(this.sheltered ? 'interior-rain' : 'rain', 0);

    // 赵呈博：自己生成、自己跟随（落座时解除）
    this.zhao = this.spawnActor(CAST.zhao, 'zhao-chengbo', p.x, p.y + 6, p.facing);
    this.attachFollower();

    // 姿态导演：坐着的人 sit，老板 wipe，其余 idle；之后台词 / 表情 / 零星举杯都经它落到图集真有的帧
    this.director = new InnPoseDirector(this, this.ctx?.seed ?? 1157);
    for (const [id, a] of this.actors) this.director.add(id, a, SEATED_IDS.has(id));

    // 对白与演出
    const ctx = this.ctx;
    const useStory = !!ctx && !this.showcase;
    const hooks: DialogueHooks = useStory ? hooksFromContext(ctx) : this.localHooks();
    const state: CinematicStateAccess = useStory ? stateFromContext(ctx) : this.localState();
    this.dialogue = createDialogueRunner(resolveDialogueBox(this), hooks, {
      host: {
        emote: (actor, emote) => { void this.emoteWithPose(actor, emote, 1400); },
        face: (actor, facing) => this.actors.get(actor)?.setFacing(facing),
        sfx: (key) => audio().sfx(key),
        wait: (ms) => this.delay(ms),
        onLine: (node) => this.director?.line(node.speaker, node.emotion),
      },
    });
    const deps = ctx ? { dialogue: this.dialogue, ctx, state } : { dialogue: this.dialogue, state };
    this.cine = createCinematicPlayer(this.guardHost(createSceneCinematicHost(this, deps)));

    bus.onTyped(Events.DIALOGUE_LINE, this.onLine);
    bus.onTyped(Events.DIALOGUE_END, this.onDialogueEnd);
    if (this.showcase) {
      this.hint = this.add.text(GAME_W - 6, GAME_H - 6, '大海龟旅店 · 方向键移动 · Z 互动 · Esc 跳过演出', { fontFamily: FONT_BODY, fontSize: '11px', color: '#e2c48c', stroke: '#101418', strokeThickness: 3 })
        .setOrigin(1).setScrollFactor(0).setDepth(Depth.UI - 1);
    }
    this.installDebugHandle();
    this.resolveBeat();
  }

  protected override onShutdown(): void {
    this.alive = false;
    bus.offTyped(Events.DIALOGUE_LINE, this.onLine);
    bus.offTyped(Events.DIALOGUE_END, this.onDialogueEnd);
    try { this.dialogue?.abort(); } catch { /* ignore */ }
    this.detachFollower();
    if (this.frozen) { try { this.anims.resumeAll(); } catch { /* ignore */ } }
    this.frozen = false;
    this.mainBusy = false;
    this.director = null;
    this.wrapped.clear();
    this.zhao = null;
    this.hint = null;
    this.yielded.clear();
    if (window.__nantianInn) window.__nantianInn.ready = false;
  }

  protected override onFixedStep(dt: number): void {
    if (!this.alive) return;
    const p = this.player;
    const sh = isSheltered(p.x, p.y, this.map);
    if (sh !== this.sheltered) { this.sheltered = sh; this.setWeather(sh ? 'interior-rain' : 'rain'); }
    this.director?.tick(dt, this.ambientPoses());
    this.tickChatter(dt);
    this.tickCrowd();
  }

  protected override onInteract(kind: InteractKind, i: MapInteraction): void {
    if (kind === 'trigger') {
      if (i.cinematic === 'inn-sit-down') void this.runMainLine();
      else if (i.cinematic) void this.playSide(i.cinematic);
      return;
    }
    if (i.dialogue) void this.runDialogue(i.dialogue, kind === 'talk' ? i : undefined);
  }

  protected override onExit(exit: MapExit | null): void {
    if (!exit || this.stage !== 'free' || this.mainBusy || this.inputLocked) return;
    if (this.showcase) return; // 展示模式不离开旅店
    void this.goToExit(exit);
  }

  // ------------------------------------------------------------------ beats
  /** 进入场景时按剧情状态决定从哪一拍开始（不在演出中途续玩）。 */
  private resolveBeat(): void {
    const story = this.ctx?.story;
    if (story && !this.showcase) {
      if (story.has('OMEN_END')) { this.stage = 'done'; void this.ctx?.enterRoute(); return; }
      if (story.has('INN_ARGUMENT')) {
        this.placePartyAtTable();
        this.setInputLocked(true);
        void this.delay(600).then(() => this.playOmen());
        return;
      }
    }
    this.stage = 'free';
    if (!this.hasMark('inn:arrived')) void this.delay(250).then(() => this.playSide('inn-arrive'));
  }

  /** 主线：坐下 → 点酒环顾 → 争论（INN_ARGUMENT）→ 预感（OMEN_END）→ 章末。 */
  private async runMainLine(): Promise<void> {
    if (!this.alive || !this.cine || this.mainBusy || this.stage !== 'free') return;
    this.mainBusy = true;
    this.stage = 'seated';
    this.detachFollower();
    this.setInputLocked(true);
    await this.cine.play('inn-sit-down');
    if (!this.alive) return;
    this.setInputLocked(true);
    await this.cine.play('inn-argument');
    if (!this.alive) return;
    await this.playOmen();
  }

  private async playOmen(): Promise<void> {
    if (!this.alive || !this.cine) return;
    this.mainBusy = true;
    this.stage = 'argued';
    this.setInputLocked(true);
    try { audio().setAmbience(AmbiencePresets['omen-end'] ?? [], 1800); } catch { /* ignore */ }
    await this.cine.play('inn-omen');
    if (!this.alive) return;
    this.stage = 'done';
    if (this.ctx && !this.showcase) { await this.ctx.enterRoute(); return; }
    // 展示模式：淡入后从门口重来
    await this.delay(800);
    if (!this.alive) return;
    const ui = this.scene.get(SceneKeys.UI) as (Phaser.Scene & { fade?: (out: boolean, ms: number) => Promise<void> }) | null;
    if (ui?.fade && ui.scene.isActive()) void ui.fade(false, 400);
    this.scene.restart({ spawn: 'inn-entrance' } satisfies SceneEntryData);
  }

  /** 可选的小演出（进店一眼等）：不改主线进度。 */
  private async playSide(id: string): Promise<void> {
    if (!this.alive || !this.cine || this.mainBusy || this.cine.isPlaying()) return;
    this.setInputLocked(true);
    try { await this.cine.play(id); } finally { if (this.alive && !this.mainBusy) this.setInputLocked(false); }
  }

  private async runDialogue(id: string, via?: MapInteraction): Promise<void> {
    if (!this.alive || !this.dialogue || this.dialogue.isRunning() || this.mainBusy || this.cine?.isPlaying()) return;
    this.setInputLocked(true);
    const npc = via ? this.npcFor(via) : null;
    npc?.faceTowards(this.player.x, this.player.y);
    try { await this.dialogue.run(id); } finally { if (this.alive && !this.mainBusy) this.setInputLocked(false); }
  }

  /** 续玩到争论之后：大家已经在桌边，罗大湖眼睛暗了下来（dim 帧保持）。 */
  private placePartyAtTable(): void {
    this.detachFollower();
    const d = this.director;
    const seat = (id: string, a: IActor | null | undefined, x: number, y: number, facing: Facing) => {
      if (!a) return;
      a.setPosition(x, y, facing);
      if (d?.has(id)) d.setRest(id, 'sit'); else a.play('sit');
    };
    seat(CAST.player, this.player, SEATS.player.x, SEATS.player.y, SEATS.player.facing);
    seat(CAST.zhao, this.zhao, SEATS.zhao.x, SEATS.zhao.y, SEATS.zhao.facing);
    seat(CAST.luo, this.actors.get(CAST.luo), SEATS.luo.x, SEATS.luo.y, 'down');
    seat(CAST.wu, this.actors.get(CAST.wu), SEATS.wu.x, SEATS.wu.y, SEATS.wu.facing);
    d?.emote(CAST.luo, 'dim');
    this.sheltered = true;
    this.setWeather('interior-rain', 0);
    this.cameraFollowPlayer();
  }

  // ------------------------------------------------------------------ poses
  /** 零星举杯 / 邻桌同笑只在自由探索与落座演出期间；预感段与定格里店里安静下来。 */
  private ambientPoses(): boolean { return !this.frozen && (this.stage === 'free' || this.stage === 'seated'); }

  /** 表情：有身体帧就摆姿态（`fallback` 类表情不再弹气泡），否则弹气泡（正式别名图优先）。 */
  private emoteWithPose(id: string, emote: string, ms?: number): Promise<void> {
    const a = this.actors.get(id);
    if (!a) return Promise.resolve();
    const r = this.director?.emote(id, emote, ms) ?? { bubble: true, pose: null };
    if (!r.bubble) return this.delay(ms && ms > 0 ? ms : 1200);
    return a.showEmote(emoteIcon((k) => this.textures.exists(k), emote), ms);
  }

  /** 时间线 `anim` 步：sit / idle 改变休息姿态（导演记住并按图集落帧），附带的表情走姿态路径。 */
  private playState(id: string, a: IActor, state: ActorAnimState, emote?: string): void {
    if (state !== 'emote' && this.director?.has(id)) this.director.setRest(id, state);
    else if (state !== 'emote') a.play(state);
    if (emote) void this.emoteWithPose(id, emote);
  }

  /** 演出用的演员代理：emote / play 经姿态导演，其余原样转发（同一演员只包一次）。 */
  private wrapActor(id: string, a: IActor | undefined): IActor | undefined {
    if (!a) return undefined;
    const cached = this.wrapped.get(id);
    if (cached && cached.inner === a) return cached.proxy;
    const scene = this;
    const proxy: IActor = {
      get id() { return a.id; },
      get sprite() { return a.sprite; },
      get displayName() { return a.displayName; },
      get x() { return a.x; }, set x(v: number) { a.x = v; },
      get y() { return a.y; }, set y(v: number) { a.y = v; },
      get facing() { return a.facing; }, set facing(f: Facing) { a.facing = f; },
      setFacing: (f) => a.setFacing(f),
      play: (state, emote) => scene.playState(id, a, state, emote),
      walkTo: (x, y, speed) => a.walkTo(x, y, speed),
      isMoving: () => a.isMoving(),
      setPosition: (x, y, f) => a.setPosition(x, y, f),
      stop: () => a.stop(),
      faceTowards: (x, y) => a.faceTowards(x, y),
      showEmote: (emote, ms) => scene.emoteWithPose(id, emote, ms),
      setControllable: (on) => a.setControllable(on),
      destroy: () => a.destroy(),
    };
    this.wrapped.set(id, { inner: a, proxy });
    return proxy;
  }

  // ------------------------------------------------------------------ followers / npcs
  private attachFollower(): void {
    const p = this.player, z = this.zhao;
    if (this.zhaoFollower || !isCharacterActor(p) || !isCharacterActor(z)) return;
    try { this.zhaoFollower = new Follower(this, z, p, { slot: 1 }); }
    catch (e) { console.warn('[turtle-inn] follower failed; 赵呈博 stays put', e); }
  }
  private detachFollower(): void {
    if (!this.zhaoFollower) return;
    try { this.zhaoFollower.destroy(); } catch { /* ignore */ }
    this.zhaoFollower = null;
  }

  /** 交谈对象：与该交互共用对白 id 的 NPC 中离交互矩形最近的一个。 */
  private npcFor(i: MapInteraction): IActor | null {
    const cx = i.x + i.w / 2, cy = i.y + i.h / 2;
    let best: IActor | null = null, bestD = Infinity;
    for (const n of INN_NPCS) {
      if (n.dialogue !== i.dialogue) continue;
      const a = this.actors.get(n.id);
      if (!a) continue;
      const d = (a.x - cx) ** 2 + (a.y - cy) ** 2;
      if (d < bestD) { bestD = d; best = a; }
    }
    return best;
  }

  /** 站着的客人：玩家挤过来时向侧面挪一格让路（“好不容易挤了进去”）。 */
  private tickCrowd(): void {
    if (this.stage !== 'free' || this.inputLocked) return;
    const p = this.player;
    for (const id of CROWD_IDS) {
      const a = this.actors.get(id);
      if (!a) continue;
      const dx = p.x - a.x, dy = p.y - a.y, d2 = dx * dx + dy * dy;
      if (this.yielded.has(id)) { if (d2 > UNYIELD_DIST * UNYIELD_DIST) this.yielded.delete(id); continue; }
      if (d2 > YIELD_DIST * YIELD_DIST || a.isMoving()) continue;
      this.yielded.add(id);
      const sideways = Math.abs(dx) < Math.abs(dy); // 玩家从上下靠近 → 向左右让
      const cands = sideways ? [{ x: a.x - 32, y: a.y }, { x: a.x + 32, y: a.y }] : [{ x: a.x, y: a.y - 32 }, { x: a.x, y: a.y + 32 }];
      cands.sort((c1, c2) => ((c2.x - p.x) ** 2 + (c2.y - p.y) ** 2) - ((c1.x - p.x) ** 2 + (c1.y - p.y) ** 2));
      const target = cands.find((c) => !this.isBlocked(c.x - 10, c.y - 6) && !this.isBlocked(c.x + 9, c.y - 6) && !this.isBlocked(c.x, c.y - 1));
      if (!target) continue;
      void a.showEmote('exclaim', 500);
      void a.walkTo(target.x, target.y, 60);
    }
  }

  /** 空间闲聊：店里各桌零星的碰杯与笑声，来源位置决定音量与声像；哄笑时那桌有 laugh 帧的人一起笑。 */
  private tickChatter(dt: number): void {
    this.chatterLeft -= dt;
    if (this.chatterLeft > 0) return;
    this.chatterLeft = this.chatterRng.range(CHATTER_MIN_MS, CHATTER_MAX_MS);
    if (this.stage === 'done' || this.frozen) return;
    const src = this.chatterRng.pick(CHATTER_SOURCES);
    const key = this.chatterRng.pick(CHATTER_SFX);
    try { audio().sfx(key, { x: src.x, y: src.y, volume: 0.35 }); } catch { /* ignore */ }
    if (key === LAUGH_SFX && this.ambientPoses()) this.director?.laughNear(src.x, src.y);
  }

  // ------------------------------------------------------------------ cinematic host guard
  /** 场景关闭后一切演出步骤都变成空操作（不许在别的场景里弹对白、淡出或改旗标）；定格步冻结动画；演员经姿态导演代理。 */
  private guardHost(base: CinematicHost): CinematicHost {
    const ok = () => this.alive;
    const cam = base.camera;
    const host: CinematicHost = {
      say: (id) => (ok() ? base.say(id) : Promise.resolve()),
      sayFastForward: (id) => { if (ok()) base.sayFastForward?.(id); },
      onSkip: () => { if (ok()) base.onSkip?.(); },
      setFlag: (f) => { if (ok()) base.setFlag(f); },
      setMark: (m) => { if (ok()) base.setMark(m); },
      hasMark: (m) => (base.hasMark ? base.hasMark(m) : false),
      wait: (ms) => (ok() ? base.wait(ms) : Promise.resolve()),
      actor: (id) => (ok() ? this.wrapActor(id, base.actor?.(id)) : undefined),
      weather: (s, ms) => { if (ok()) base.weather?.(s, ms); },
      fx: (effect, params) => {
        if (!ok() || !base.fx) return Promise.resolve();
        if (effect === 'freeze-frame') this.freeze();
        return base.fx(effect, params);
      },
      sfx: (k) => { if (ok()) base.sfx?.(k); },
      music: (k, f) => { if (ok()) base.music?.(k, f); },
      fade: (out, ms, color) => (ok() && base.fade ? base.fade(out, ms, color) : Promise.resolve()),
      setInputLocked: (l) => { if (ok()) base.setInputLocked?.(l); },
      registerSkip: (fn) => base.registerSkip?.(fn),
    };
    if (cam) {
      host.camera = {
        panTo: (x, y, ms) => (ok() ? cam.panTo(x, y, ms) : Promise.resolve()),
        follow: () => { if (ok()) cam.follow(); },
        shake: (ms, i) => { if (ok()) cam.shake(ms, i); },
        zoom: (z, ms) => (ok() && cam.zoom ? cam.zoom(z, ms) : Promise.resolve()),
      };
    }
    return host;
  }

  /** 定格：动画全停，赵呈博“眉头紧缩”固定住，闲聊静默。雨照下——那一刻停住的是人。 */
  private freeze(): void {
    if (this.frozen) return;
    this.frozen = true;
    const z = this.zhao;
    if (z) { z.setFacing('left'); void z.showEmote(emoteIcon((k) => this.textures.exists(k), 'frown'), 0); }
    try { this.anims.pauseAll(); } catch { /* ignore */ }
  }

  // ------------------------------------------------------------------ helpers
  private delay(ms: number): Promise<void> {
    return new Promise<void>((resolve) => {
      if (!this.alive || ms <= 0) { resolve(); return; }
      let done = false;
      const finish = () => { if (done) return; done = true; this.events.off(Phaser.Scenes.Events.SHUTDOWN, finish); resolve(); };
      this.events.once(Phaser.Scenes.Events.SHUTDOWN, finish);
      this.time.delayedCall(ms, finish);
    });
  }

  private hasMark(m: string): boolean {
    if (this.ctx && !this.showcase) return this.ctx.story.hasMark(m);
    return this.localMarks.has(m);
  }

  private localHooks(): DialogueHooks {
    return {
      hasFlag: (f) => this.localFlags.has(f),
      hasMark: (m) => this.localMarks.has(m),
      setMark: (m) => { this.localMarks.add(m); },
      setFlag: (f) => { this.localFlags.add(f); },
      weather: () => this.weather,
      instant: () => this.ctx?.settings.textSpeed === 'instant',
    };
  }
  private localState(): CinematicStateAccess {
    return {
      hasMark: (m) => this.localMarks.has(m),
      setMark: (m) => { this.localMarks.add(m); },
      setFlag: (f) => { this.localFlags.add(f); },
      hasFlag: (f) => this.localFlags.has(f),
    };
  }

  private installDebugHandle(): void {
    const handle: InnDebugHandle = {
      ready: true,
      getState: () => ({
        stage: this.stage,
        flag: this.showcase ? [...this.localFlags].pop() ?? null : this.ctx?.story.current() ?? null,
        weather: this.weather, sheltered: this.sheltered, locked: this.inputLocked,
        player: { x: this.player.x, y: this.player.y, facing: this.player.facing },
        zhao: this.zhao ? { x: this.zhao.x, y: this.zhao.y } : null,
        prompt: this.currentInteraction?.id ?? null,
        dialogue: this.dialogue?.currentDialogue() ?? null,
        cinematic: this.cine?.currentTimeline() ?? null,
        lastLine: this.lastLine, actors: this.actors.size, frozen: this.frozen,
      }),
      sit: async () => {
        if (this.stage !== 'free' || this.mainBusy) return;
        this.player.setPosition(SEATS.player.x + 32, SEATS.player.y, 'left');
        await this.runMainLine();
      },
      skip: () => this.cine?.skip() ?? false,
      interact: () => this.interact()?.id ?? null,
      teleport: (x, y, facing) => { this.player.setPosition(x, y, facing); this.zhaoFollower?.reset(); },
      omen: () => this.playOmen(),
      poses: () => this.director?.snapshot() ?? {},
      art: () => this.director?.textures() ?? {},
    };
    window.__nantianInn = handle;
  }
}
